import { useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { PhotoUploadDropzone } from '../../atoms/PhotoUploadDropzone/PhotoUploadDropzone';
import { Badge } from '../../atoms/Badge';
import { Button } from '../../atoms/Button';
import { Inline } from '../../atoms/Inline';
import { Stack } from '../../atoms/Stack';
import { Text } from '../../atoms/Typography';
import styles from './IdentifyCaptureStep.module.css';
import { Alert } from '../../atoms/Alert';
import { LiveRegion } from '../../atoms/LiveRegion';

const MAX_PHOTO_SIZE_MB = 10;

export interface IdentifyCaptureStepProps {
  /** Called with the selected file when the user confirms upload. */
  onSubmit: (file: File) => void;
  /** Called when the user cancels — at any time, including while uploading. Creates nothing. */
  onCancel: () => void;
  /** Whether an upload/analysis request is currently in flight. */
  isLoading: boolean;
  /**
   * Set when the previous attempt failed: the translated reason the flow chose (AI credits
   * used up, unsupported/too large photo, service error) — never raw backend text.
   */
  error?: string | null;
  /** Called from "Try again" so the flow can clear its error state. */
  onRetry?: () => void;
}

/**
 * Step 1 of "Identify from photo" (epic #1459), rendered inside the LivestockIdentifyFlow
 * dialog (#1507): choose or take a photo, then upload it for identification. The dialog
 * title names the step, so the step itself has no second heading for it.
 */
export function IdentifyCaptureStep({ onSubmit, onCancel, isLoading, error = null, onRetry }: IdentifyCaptureStepProps) {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleRetry = () => {
    setSelectedFile(null);
    onRetry?.();
  };

  if (error) {
    return (
      <Stack gap="4" className={styles.container}>
        <Alert title={t('identify.capture.error.title')} titleLevel={3}>
          {error}
        </Alert>
        <Inline gap="2" wrap>
          <Button variant="secondary" onClick={handleRetry}>
            {t('identify.capture.error.retry')}
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            {t('identify.capture.cancel')}
          </Button>
        </Inline>
      </Stack>
    );
  }

  return (
    <Stack gap="4" className={styles.container}>
      <Text color="muted">{t('identify.capture.instructions')}</Text>
      <Text color="muted">{t('identify.capture.lightingTip')}</Text>

      <PhotoUploadDropzone
        onChange={setSelectedFile}
        maxSizeMB={MAX_PHOTO_SIZE_MB}
        disabled={isLoading}
        ariaLabel={t('identify.capture.dropzoneLabel')}
        previewAlt={t('identify.capture.previewAlt')}
      />

      {isLoading && (
        <LiveRegion>
          <Stack gap="2">
            <Text>{t('identify.capture.uploading')}</Text>
            <Inline gap="2">
              <Badge tone="info">{t('identify.capture.pleaseWait')}</Badge>
            </Inline>
          </Stack>
        </LiveRegion>
      )}

      <Inline gap="2" wrap justify="end">
        {/* Cancel stays available while uploading: the flow aborts the request and creates nothing. */}
        <Button variant="ghost" onClick={onCancel}>
          {t('identify.capture.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={() => selectedFile && onSubmit(selectedFile)}
          disabled={!selectedFile || isLoading}
          isLoading={isLoading}
        >
          {t('identify.capture.submit')}
        </Button>
      </Inline>
    </Stack>
  );
}
